// The according grouping spec:
// https://docs.google.com/document/d/1O8OhpUs7zgtPXhheK0eBmHoO97cu-qjitFnfD8zZ8hU/edit#heading=h.bcskquw57c3t
import { ArdoqId, GroupingRule, GroupType } from '@ardoq/api-types';
import { GraphModelShape } from '@ardoq/data-model';
import { last, uniq } from 'lodash';
import {
  CloneIdToComponentIdMap,
  ClonedComponentsMap,
  ComponentGroupsMap,
  ComponentMap,
  GroupByArgs,
  GroupMap,
  GroupedComponents,
  GroupingResult,
  ParentGroupMap,
  ProcessedReferences,
  RawGraphNode2,
  ViewError,
} from '../../types';
import { isTruthy } from '@ardoq/common-helpers';
import {
  buildParentGroups,
  groupByReferenceType,
  groupByReferenceTypeName,
} from './groupByReferenceTypes';
import {
  isParentTypeGrouping,
  toPathObject,
  updateParent,
} from './buildGroupsUtils';
import { isUseNewGrouping } from './isUseNewGrouping';
import { insertParentGroups } from './insertParentGroups';
import { PathList } from './PathList';
import {
  isDateFieldType,
  normalizeToLocalDate,
  parseDate,
  startOfDay,
} from '@ardoq/date-time';
import type { GraphInterface } from '../../graphInterface/graphInterface';
import { type I18nLocale, t } from '@ardoq/i18n';

// The according grouping spec:
// https://docs.google.com/document/d/1O8OhpUs7zgtPXhheK0eBmHoO97cu-qjitFnfD8zZ8hU/edit#heading=h.bcskquw57c3t

type BuildGroupsArgs = {
  graphInterface: GraphInterface;
  graph: GraphModelShape;
  groupingRules: GroupingRule[];
  componentIds: ArdoqId[];
  referenceIds: ArdoqId[];
  canBeUsedAsGroup: (componentId: ArdoqId) => boolean;
  i18nLocale: I18nLocale;
  isParentRelationAsReference?: boolean;
  useNewGrouping?: boolean;
};

type BuildGroupsResult = {
  groupMap: GroupMap;
  componentGroupsMap: ComponentGroupsMap;
  componentMap: ComponentMap;
  errors: ViewError[];
  rootGroups: RawGraphNode2[];
  processedReferences: ProcessedReferences;
  clonedComponentsMap: ClonedComponentsMap;
};

const buildGroups = ({
  graphInterface,
  graph,
  groupingRules,
  componentIds,
  referenceIds,
  canBeUsedAsGroup,
  i18nLocale,
  isParentRelationAsReference = false,
  useNewGrouping = false,
}: BuildGroupsArgs): BuildGroupsResult => {
  const groupMap: GroupMap = new Map();
  const groupMapRaw: GroupMap = new Map();
  const parentGroupMap: ParentGroupMap = new Map();
  const componentGroupsMap: ComponentGroupsMap = new Map();
  const groupedComponents: GroupedComponents = new Set();
  const clonedComponentsMap: ClonedComponentsMap = new Map();
  const processedReferences: ProcessedReferences = new Set();
  const cloneIdToComponentIdMap: CloneIdToComponentIdMap = new Map();
  const errors: ViewError[] = [];
  const componentMap: ComponentMap = new Map();
  const pathList = new PathList();

  const result: GroupingResult = {
    groupMap,
    groupMapRaw,
    parentGroupMap,
    componentGroupsMap,
    groupedComponents,
    clonedComponentsMap,
    cloneIdToComponentIdMap,
    processedReferences,
    errors,
    isParentRelationAsReference,
    componentMap,
    pathList,
  };

  groupingRules.forEach((groupBy, index) => {
    const grouper = grouperMap.get(groupBy.type);
    if (grouper) {
      grouper({
        graph,
        componentIds,
        referenceIds,
        groupBy,
        index,
        result,
        canBeUsedAsGroup,
        useNewGrouping,
        graphInterface,
        i18nLocale,
      });
    }
  });

  createGroups(result, componentIds, graphInterface);

  componentIds
    .filter(
      componentId =>
        !componentMap.has(componentId) && !componentGroupsMap.has(componentId)
    )
    .forEach(componentId =>
      componentMap.set(componentId, {
        id: componentId,
        modelId: componentId,
        name:
          graphInterface.getComponentAttribute(componentId, 'name') ??
          undefined,
      })
    );

  return {
    groupMap,
    componentGroupsMap,
    componentMap,
    errors,
    rootGroups: Array.from(result.groupMap.values()).filter(
      group => !group.parentId
    ),
    processedReferences,
    clonedComponentsMap,
  };
};

export { buildGroups };

const groupByField = ({
  graphInterface,
  groupBy,
  componentIds,
  result,
}: GroupByArgs) => {
  const { targetId: fieldId = '' } = groupBy;
  const field = graphInterface.getFieldData(fieldId);
  if (!field) return;

  const { name, type: fieldType } = field;

  const isGroupByDateWithoutTime = isDateFieldType(fieldType);

  componentIds
    .filter(componentId =>
      graphInterface.isFieldApplicableToComponent(fieldId, componentId)
    )
    .forEach(componentId => {
      const value = graphInterface.getComponentAttribute(componentId, name);
      const groupKey = isGroupByDateWithoutTime
        ? startOfDay(parseDate(normalizeToLocalDate(value))).toString()
        : value;
      const label = `${graphInterface.getFieldLabel(fieldId) ?? ''} = ${
        graphInterface.formatFieldValue({
          fieldId,
          value,
          excludeTime: isGroupByDateWithoutTime,
        }) ?? ''
      }`;
      const groupIdPrefix = getFieldValueKey(name, groupKey);
      const type = GroupType.FIELD;

      insertGroup(
        graphInterface,
        componentId,
        undefined,
        groupIdPrefix,
        label,
        type,
        result,
        value
      );
    });
};

const groupByWorkspace = ({
  graphInterface,
  componentIds,
  result,
}: GroupByArgs) => {
  componentIds.forEach(componentId => {
    const rootWorkspace =
      graphInterface.getComponentWorkspaceId(componentId) ?? '';
    const groupIdPrefix = rootWorkspace;
    const label =
      graphInterface.getWorkspaceAttribute(rootWorkspace, 'name') ?? '';
    const type = GroupType.WORKSPACE;
    const modelId = rootWorkspace;
    insertGroup(
      graphInterface,
      componentId,
      modelId,
      groupIdPrefix,
      label,
      type,
      result
    );
  });
};

const groupAll = ({
  componentIds,
  groupBy,
  result,
  graphInterface,
}: GroupByArgs) => {
  componentIds.forEach(componentId => {
    const groupIdPrefix = 'all-group';
    const label = groupBy.label || 'All';
    const type = GroupType.ALL;
    insertGroup(
      graphInterface,
      componentId,
      undefined,
      groupIdPrefix,
      label,
      type,
      result
    );
  });
};

const groupByComponentType = ({
  graphInterface,
  componentIds,
  result,
}: GroupByArgs) => {
  componentIds.forEach(componentId => {
    const { name, id } = graphInterface.getComponentType(componentId) ?? {
      name: '',
      id: '',
    };
    const groupIdPrefix = id;
    const label = name;
    const type = GroupType.COMPONENT;
    const modelId = id;
    insertGroup(
      graphInterface,
      componentId,
      modelId,
      groupIdPrefix,
      label,
      type,
      result
    );
  });
};

const groupByParent = ({
  groupBy,
  componentIds,
  result,
  index,
  canBeUsedAsGroup,
  useNewGrouping,
  graph,
  graphInterface,
  i18nLocale,
}: GroupByArgs) => {
  const { targetId: parentTypeId = '' } = groupBy;
  const hasParent = getHasParentOfType(
    graphInterface,
    parentTypeId,
    canBeUsedAsGroup
  );

  genericGroupByParents({
    groupBy,
    componentIds,
    result,
    index,
    hasParent,
    type: GroupType.PARENT,
    useNewGrouping,
    graph,
    graphInterface,
    i18nLocale,
  });
};

const groupByParentAll = ({
  groupBy,
  componentIds,
  result,
  index,
  canBeUsedAsGroup,
  useNewGrouping,
  graph,
  graphInterface,
  i18nLocale,
}: GroupByArgs) => {
  const hasParent = getHasParentAll(graphInterface, canBeUsedAsGroup);

  genericGroupByParents({
    groupBy,
    componentIds,
    result,
    index,
    hasParent,
    type: GroupType.PARENT_ALL,
    useNewGrouping,
    graph,
    graphInterface,
    i18nLocale,
  });
};

const groupByTag = ({
  graphInterface,
  componentIds,
  groupBy,
  result,
}: GroupByArgs) => {
  const { targetId: tagId = '' } = groupBy;
  const tag = graphInterface.getTag(tagId);
  const taggedComponentIds = new Set(tag?.components ?? []);

  componentIds
    .filter(componentId => taggedComponentIds.has(componentId))
    .forEach(componentId => {
      const groupIdPrefix = tagId;
      const label = `#${tag?.name ?? ''}`;
      const type = GroupType.TAG;
      const modelId = tagId;
      insertGroup(
        graphInterface,
        componentId,
        modelId,
        groupIdPrefix,
        label,
        type,
        result
      );
    });
};

const groupByChild = ({ result, groupBy, index, i18nLocale }: GroupByArgs) => {
  result.errors.push({
    message: t(
      i18nLocale,
      'graph:groupingErrors.groupByChildNotSupported',
      'Grouping by child is not supported'
    ),
    rule: groupBy,
    index,
  });
};

const grouperMap = new Map([
  [GroupType.FIELD, groupByField],
  [GroupType.WORKSPACE, groupByWorkspace],
  [GroupType.COMPONENT, groupByComponentType],
  [GroupType.PARENT, groupByParent],
  [GroupType.TAG, groupByTag],
  [GroupType.PARENT_ALL, groupByParentAll],
  [GroupType.REFERENCE, groupByReferenceType],
  [GroupType.REFERENCE_TYPE_NAME, groupByReferenceTypeName],
  [GroupType.REFERENCE_CONTEXTUAL, groupByReferenceType],
  [GroupType.CHILD, groupByChild],
  [GroupType.ALL, groupAll],
]);

const getCloneIdCreator = () => {
  let cloneId = 1;
  return () => `clone-${cloneId++}`;
};

const createGroups = (
  result: GroupingResult,
  componentIds: ArdoqId[],
  graphInterface: GraphInterface
) => {
  const getCloneId = getCloneIdCreator();
  const processedPaths = new Set<string>();

  result.pathList
    .sort(({ path: pathA }, { path: pathB }) => pathB.length - pathA.length)
    .forEach(({ path, sublabels }) =>
      createGroupFromPath(
        path,
        result,
        componentIds,
        processedPaths,
        getCloneId,
        sublabels,
        graphInterface
      )
    );
};

const GroupingWithSublabels = new Set([
  GroupType.REFERENCE,
  GroupType.PARENT,
  GroupType.PARENT_ALL,
]);

const createGroupFromPath = (
  path: string[],
  result: GroupingResult,
  componentIds: ArdoqId[],
  processedPaths: Set<string>,
  getCloneId: () => string,
  sublabels: string[][],
  graphInterface: GraphInterface
) => {
  path.reduce<RawGraphNode2 | null>((parent, id, index) => {
    const pathObjectSublabels = sublabels[index];
    const groupId = parent ? `${parent.id}-${id}` : id;
    if (id === last(path)) {
      // paths which have been in the same process group.
      if (processedPaths.has(groupId)) {
        return null;
      }
      const graphId = result.componentMap.has(id)
        ? `${id}-${getCloneId()}`
        : id;

      processedPaths.add(groupId);

      const component: RawGraphNode2 = {
        id: graphId,
        parentId: parent?.id,
        modelId: id,
        name: graphInterface.getComponentAttribute(id, 'name') ?? undefined,
      };
      result.componentMap.set(graphId, component);
      if (graphId !== id) {
        let clones = result.clonedComponentsMap.get(id);
        if (!clones) {
          clones = [];
          result.clonedComponentsMap.set(id, clones);
        }
        clones.push(graphId);
      }
      if (parent) {
        parent.children?.push(component);
      }

      return component;
    }
    processedPaths.add(groupId);
    let group = result.groupMap.get(groupId);
    if (!group) {
      let subLabels;
      const { type } = result.groupMapRaw.get(id) ?? {};
      if (type && GroupingWithSublabels.has(type)) {
        subLabels = pathObjectSublabels;
      }
      group = {
        ...result.groupMapRaw.get(id),
        subLabels,
        children: [],
        id: groupId,
        parentId: parent?.id,
      };
      result.groupMap.set(groupId, group);
      if (componentIds.includes(id)) {
        let groupIds = result.componentGroupsMap.get(id);
        if (!groupIds) {
          groupIds = [];
          result.componentGroupsMap.set(id, groupIds);
        }
        groupIds.push(groupId);
      }
      if (parent) {
        parent.children?.push(group);
      }
    } else if (
      pathObjectSublabels &&
      group.type &&
      GroupingWithSublabels.has(group.type)
    ) {
      group.subLabels = uniq([
        ...(group.subLabels || []),
        ...pathObjectSublabels,
      ]).filter(isTruthy);
    }

    return group;
  }, null);
};

const insertGroup = (
  graphInterface: GraphInterface,
  componentId: ArdoqId,
  modelId: string | undefined,
  groupIdPrefix: string,
  label: string,
  type: GroupType,
  result: GroupingResult,
  fieldGroupValue?: any
) => {
  if (!result.groupMapRaw.has(groupIdPrefix)) {
    result.groupMapRaw.set(groupIdPrefix, {
      // Will be set when the actual group gets created.
      id: '',
      label,
      modelId,
      type,
      fieldGroupValue,
    });
  }

  const isAlreadyGrouped = result.pathList.ids.has(componentId);
  if (isAlreadyGrouped) {
    const pathObjects = result.pathList.map(pathObject => {
      if (!pathObject.ids.has(componentId)) {
        return pathObject;
      }

      return toPathObject(
        graphInterface,
        ...pathObject.path.reduce<[path: string[], sublabels: string[][]]>(
          ([path, sublabels], id, index) => {
            if (id === componentId) {
              const currentParent = pathObject.path[index - 1];
              updateParent(
                id,
                groupIdPrefix,
                result.parentGroupMap,
                currentParent
              );
              if (currentParent) {
                updateParent(
                  groupIdPrefix,
                  currentParent,
                  result.parentGroupMap
                );
              }
              return [
                [...path, groupIdPrefix, id],
                [...sublabels, [], pathObject.sublabels[index]],
              ];
            }
            return [
              [...path, id],
              [...sublabels, pathObject.sublabels[index]],
            ];
          },
          [[], []]
        )
      );
    });
    result.pathList = new PathList();
    result.pathList.addPaths(pathObjects);
  } else {
    result.pathList.push(
      toPathObject(graphInterface, [groupIdPrefix, componentId], [[], []])
    );
    updateParent(componentId, groupIdPrefix, result.parentGroupMap);
  }

  result.groupedComponents.add(componentId);
};

const getFieldValueKey = (fieldName: string, value: any) => {
  if (value === null || value === undefined) {
    return fieldName;
  }
  if (Array.isArray(value)) {
    // oxlint-disable-next-line require-array-sort-compare
    return `${fieldName}_${[...value].sort().join(',')}`;
  }
  return `${fieldName}_${String(value).toLowerCase()}`;
};

type GenericGroupByParentsArgs = {
  groupBy: GroupingRule;
  componentIds: ArdoqId[];
  result: GroupingResult;
  index: number;
  hasParent: (componentId: ArdoqId) => boolean;
  type: GroupType;
  useNewGrouping: boolean;
  graph: GraphModelShape;
  graphInterface: GraphInterface;
  i18nLocale: I18nLocale;
};

const genericGroupByParents = ({
  groupBy,
  componentIds,
  result,
  index,
  hasParent,
  type,
  useNewGrouping,
  graph,
  graphInterface,
  i18nLocale,
}: GenericGroupByParentsArgs) => {
  const componentsWithParentsOfType = getComponentsWithParentsOfType(
    graphInterface,
    componentIds,
    hasParent
  );

  if (isUseNewGrouping(useNewGrouping) && isParentTypeGrouping(type)) {
    insertParentGroups(
      graphInterface,
      componentsWithParentsOfType,
      groupBy,
      index,
      result,
      useNewGrouping,
      i18nLocale
    );
  } else {
    buildParentGroups({
      parentLists: componentsWithParentsOfType,
      groupBy,
      type,
      index,
      result,
      useNewGrouping: false,
      referenceTypes: graph.referenceTypes,
      graphInterface,
      i18nLocale,
    });
  }
};

const getHasParentOfType =
  (
    graphInterface: GraphInterface,
    parentTypeId: ArdoqId,
    canBeUsedAsGroup: (componentId: ArdoqId) => boolean = () => true
  ) =>
  (componentId: ArdoqId) => {
    const parentId = graphInterface.getComponentParentId(componentId);
    return parentId
      ? canBeUsedAsGroup(parentId) &&
          graphInterface.getComponentTypeId(parentId) === parentTypeId
      : false;
  };

// Returns a list of ancestor ids as long as hasParentOfType retunrs true.
// It includes the starting component and the last parent.
const getParentsOfType = (
  graphInterface: GraphInterface,
  componentId: ArdoqId,
  hasParentOfType: (componentId: ArdoqId) => boolean,
  acc: ArdoqId[] = [componentId]
): ArdoqId[] => {
  if (hasParentOfType(componentId)) {
    const parent = graphInterface.getComponentParentId(componentId) ?? '';
    return getParentsOfType(graphInterface, parent, hasParentOfType, [
      parent,
      ...acc,
    ]);
  }
  return acc;
};

const getHasParentAll =
  (
    graphInterface: GraphInterface,
    canBeUsedAsGroup: (componentId: ArdoqId) => boolean = () => true
  ) =>
  (componentId: ArdoqId) => {
    const parentId = graphInterface.getComponentParentId(componentId);
    return Boolean(parentId && canBeUsedAsGroup(parentId));
  };

const getComponentsWithParentsOfType = (
  graphInterface: GraphInterface,
  componentIds: ArdoqId[],
  hasParent: (componentId: ArdoqId) => boolean
) =>
  componentIds
    .map(componentId =>
      getParentsOfType(graphInterface, componentId, hasParent)
    )
    // the starting component is always included
    .filter(componentIds => componentIds.length > 1);
